const SCALAR_TYPES = ['string', 'integer', 'boolean']

const descriptorType = (descriptor, property, fallback = 'mixed') => (
    descriptor && descriptor[property] ? descriptor[property] : fallback
)

const findComponent = (components, reference) => {
    if (!reference || !reference.key) {
        return null
    }

    return components.find(component => (
        component.key === reference.key
        && component.version === reference.version
    )) || null
}

export const isConditionalPipelineElement = element => Boolean(
    element
    && typeof element === 'object'
    && !element.key
    && element.when
    && Array.isArray(element.then)
)

export const joinPipelineTypes = (left = 'mixed', right = 'mixed') => {
    if (left === right) {
        return left
    }
    if (left === 'mixed' || right === 'mixed') {
        return 'mixed'
    }

    const leftIsScalar = left === 'scalar' || SCALAR_TYPES.includes(left)
    const rightIsScalar = right === 'scalar' || SCALAR_TYPES.includes(right)
    return leftIsScalar && rightIsScalar ? 'scalar' : 'mixed'
}

export const pipelineTypeAccepted = (produced = 'mixed', accepted = 'mixed') => (
    accepted === 'mixed'
    || produced === accepted
    || (accepted === 'scalar' && (produced === 'scalar' || SCALAR_TYPES.includes(produced)))
)

const conditionIssues = (condition, currentType, components) => {
    if (!condition || !condition.key) {
        return ['Condition is required.']
    }

    const descriptor = findComponent(components, condition)
    if (!descriptor) {
        return [`Condition ${condition.key}@${condition.version} is not available.`]
    }

    const inputType = descriptorType(descriptor, 'inputType')
    return pipelineTypeAccepted(currentType, inputType)
        ? []
        : [`Condition expects ${inputType}, but receives ${currentType}.`]
}

const analyzeElements = (elements, components, initialType, branchMode) => {
    let currentType = initialType
    const analyzedElements = elements.map(element => {
        const inputType = currentType

        if (isConditionalPipelineElement(element)) {
            const issues = conditionIssues(element.when, inputType, components)
            if (!element.then.length) {
                issues.push('At least one then step is required.')
            }

            const branch = analyzeElements(element.then, components, inputType, true)
            const outputType = joinPipelineTypes(inputType, branch.outputType)
            currentType = outputType

            return {
                kind: 'conditional',
                inputType,
                outputType,
                issues: [...issues, ...branch.issues],
                then: branch.elements,
            }
        }

        const descriptor = findComponent(components, element)
        const issues = []
        if (!descriptor) {
            issues.push(`Component ${element.key}@${element.version} is not available.`)
        }

        const componentInputType = descriptorType(descriptor, 'inputType')
        const componentOutputType = descriptorType(descriptor, 'outputType', inputType)
        const receivedType = branchMode && descriptor && descriptor.kind === 'value_extractor'
            ? 'object'
            : inputType

        if (descriptor && !pipelineTypeAccepted(receivedType, componentInputType)) {
            issues.push(`Component expects ${componentInputType}, but receives ${receivedType}.`)
        }

        if (element.when) {
            issues.push(...conditionIssues(element.when, inputType, components))
        }

        const outputType = element.when
            ? joinPipelineTypes(inputType, componentOutputType)
            : componentOutputType
        currentType = outputType

        return {
            kind: 'step',
            inputType,
            outputType,
            componentInputType,
            componentOutputType,
            issues,
        }
    })

    return {
        elements: analyzedElements,
        outputType: currentType,
        issues: analyzedElements.reduce((issues, element) => [
            ...issues,
            ...element.issues,
        ], []),
    }
}

export const analyzePipelineTypeFlow = ({
    elements = [],
    components = [],
    initialType = 'mixed',
    branchMode = false,
    targetType = null,
} = {}) => {
    const analysis = analyzeElements(elements, components, initialType, branchMode)
    const targetIssue = targetType && !pipelineTypeAccepted(analysis.outputType, targetType)
        ? `Canonical target expects ${targetType}, but pipeline produces ${analysis.outputType}.`
        : null

    return {
        ...analysis,
        targetIssue,
        issues: targetIssue ? [...analysis.issues, targetIssue] : analysis.issues,
    }
}

